import type {ToolUserRequestedMessage, SessionState} from '../../types/session'
import {isPermissionRequestArguments, SessionState as SessionStateValues} from '../../types/session'
import {ResolvedPermission, type PermissionEntryState} from './ResolvedPermission'

export type PermissionEntryStatus = {
  state: PermissionEntryState
  denialKind?: string
}

interface PermissionLogEntryProps {
  prompt: ToolUserRequestedMessage
  resolvedAnswer?: string
  isAborted?: boolean
  sessionState?: SessionState
  isPending?: boolean
}

/**
 * Pick the visual state for a permission_request entry, or `null` when the
 * entry should stay hidden. Resolved permission states are represented by the
 * tool execution/denial row; only unresolved pending/expired prompts render.
 */
export function getPermissionEntryStatus({
  resolvedAnswer,
  isAborted,
  isPending,
  sessionState,
}: {
  resolvedAnswer?: string
  isAborted?: boolean
  isPending: boolean
  sessionState?: SessionState
}): PermissionEntryStatus | null {
  if (resolvedAnswer || isAborted) return null
  if (!isPending) return null
  if (sessionState === SessionStateValues.TimedOut) {
    return {state: 'expired'}
  }
  return {state: 'pending'}
}

/** Renders a permission_request prompt in the session-logs timeline. */
export function PermissionLogEntry({
  prompt,
  resolvedAnswer,
  isAborted,
  sessionState,
  isPending,
}: PermissionLogEntryProps) {
  const {arguments: args} = prompt

  if (!isPermissionRequestArguments(args)) {
    return null
  }

  const status = getPermissionEntryStatus({
    resolvedAnswer,
    isAborted,
    isPending: !!isPending && !resolvedAnswer && !isAborted,
    sessionState,
  })
  if (!status) {
    return null
  }

  let detail = args.command || args.filePath
  if (!detail && args.kind === 'hook' && args.toolArgs) {
    const toolLabel = args.toolName || args.kind || 'unknown'
    detail = `Tool: ${toolLabel}\nArgs: ${args.toolArgs}`
  }

  return (
    <ResolvedPermission
      kind={args.kind}
      toolName={args.toolName}
      serverName={args.serverName}
      description={args.description}
      detail={detail}
      state={status.state}
      denialKind={status.denialKind}
    />
  )
}
